<template>
    <div class="activity-detail-page" v-loading="loading">
        <!-- Top Header / Banner -->
        <div class="apple-banner">
            <div class="container banner-inner">
                <!-- Breadcrumbs -->
                <el-breadcrumb separator="/" class="apple-breadcrumb">
                    <el-breadcrumb-item :to="{ path: '/activities' }">活动中心</el-breadcrumb-item>
                    <el-breadcrumb-item>活动详情</el-breadcrumb-item>
                </el-breadcrumb>

                <div class="apple-hero-card">
                    <el-row :gutter="0" class="hero-row">
                        <!-- Left: Cover -->
                        <el-col :md="8" :sm="24" class="cover-col">
                            <el-image :src="activity.coverImage || defaultCover" class="apple-cover" fit="cover" />
                        </el-col>
                        <!-- Right: Info -->
                        <el-col :md="16" :sm="24" class="info-col">

                            <div class="info-content">
                                <div class="tags-wrapper">
                                    <span v-for="tag in activity.tags" :key="tag" class="apple-tag">{{ tag }}</span>
                                </div>
                                <h1 class="apple-title">{{ activity.title }}</h1>
                                <p class="apple-meta">
                                    <el-icon>
                                        <View />
                                    </el-icon> {{ activity.viewCount }} 次浏览
                                    <span class="dot-separator">•</span>
                                    <el-icon>
                                        <User />
                                    </el-icon> {{ activity.enrollCount }} 人已参加
                                </p>

                                <div class="apple-action-area">
                                    <el-button v-if="!isEnrolled" type="primary" class="apple-btn"
                                        @click="handleEnroll">
                                        报名加入学习
                                    </el-button>
                                    <template v-else>
                                        <div class="apple-enrolled-badge">
                                            <el-icon>
                                                <CircleCheckFilled />
                                            </el-icon> 您已参加
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </el-col>
                    </el-row>
                </div>
            </div>
        </div>

        <!-- Main Content Tabs -->
        <div class="container main-content">
            <el-tabs v-model="activeTab" class="detail-tabs">
                <el-tab-pane label="活动介绍" name="intro">
                    <div class="intro-content card-box">
                        <md-editor v-model="activity.description" preview-only />
                    </div>
                </el-tab-pane>

                <el-tab-pane label="课程内容" name="content">
                    <div class="content-tree card-box">
                        <div v-if="!isEnrolled" class="locked-tip">
                            <el-icon>
                                <Lock />
                            </el-icon>
                            <p>请先完成报名，即可解锁所有章节和试卷</p>
                            <el-button type="primary" plain @click="handleEnroll">前往报名</el-button>
                        </div>
                        <div v-else-if="nodes.length === 0" class="empty">暂无内容</div>
                        <div v-else class="node-list">
                            <div v-for="chapter in nodes" :key="chapter.id" class="chapter-item">
                                <h3 class="chapter-title">{{ chapter.title }}</h3>
                                <div v-for="section in chapter.children" :key="section.id" class="section-item">
                                    <h4 class="section-title">{{ section.title }}</h4>
                                    <div v-for="paper in section.children" :key="paper.id" class="paper-item"
                                        @click="goPaper(paper)">
                                        <div class="paper-title-area">
                                            <el-icon>
                                                <Document />
                                            </el-icon>
                                            <span class="paper-name">{{ paper.title }}</span>
                                        </div>
                                        <div class="paper-actions">
                                            <el-button link type="primary">预览试卷</el-button>
                                            <el-button link type="success" @click.stop="openCheckInDialog(paper.id)">
                                                <el-icon>
                                                    <Check v-if="hasCheckedIn(paper.id)" />
                                                    <Edit v-else />
                                                </el-icon>
                                                {{ hasCheckedIn(paper.id) ? '已打卡' : '打卡记录' }}
                                            </el-button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="视频专区" name="videos">
                    <div class="video-grid card-box">
                        <div v-if="!isEnrolled" class="locked-tip">
                            <el-icon>
                                <Lock />
                            </el-icon>
                            <p>报名后可观看全部视频课程</p>
                        </div>
                        <div v-else-if="videos.length === 0" class="empty">暂无视频</div>
                        <div v-else class="videos">
                            <el-card v-for="video in videos" :key="video.id" class="video-card" hover
                                @click="playVideo(video)">
                                <div class="video-thumb">
                                    <el-image :src="video.coverUrl || defaultVideoCover" fit="cover" />
                                    <div class="play-overlay"><el-icon>
                                            <VideoPlay />
                                        </el-icon></div>
                                </div>
                                <div class="video-info">
                                    <div class="video-title">{{ video.title }}</div>
                                    <div class="video-meta">{{ formatDuration(video.duration) }}</div>
                                </div>
                            </el-card>
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="讨论区" name="discussion">
                    <div class="discussion-section card-box">
                        <div class="discussion-header">
                            <h3>讨论交流 ({{ posts.length }})</h3>
                            <el-button type="primary" size="small" @click="showPostDialog = true">发帖讨论</el-button>
                        </div>

                        <div class="post-list">
                            <div v-for="post in posts" :key="post.id" class="post-item" @click="goPost(post)">
                                <el-avatar :size="40" :src="post.author?.avatar" />
                                <div class="post-content">
                                    <div class="post-title">{{ post.title }}</div>
                                    <div class="post-meta">
                                        {{ post.author?.username }} · {{ formatDate(post.createdAt) }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="posts.length === 0" class="empty">暂无讨论，快来抢沙发</div>
                        </div>
                    </div>
                </el-tab-pane>

                <el-tab-pane label="打卡墙" name="checkins">
                    <div class="checkin-wall card-box">
                        <div class="wall-header">
                            <h3>全站打卡 ({{ allCheckIns.length }})</h3>
                        </div>
                        <div class="checkin-grid">
                            <el-card v-for="item in allCheckIns" :key="item.id" class="checkin-card" shadow="hover"
                                @click="viewCheckInDetail(item)">
                                <div class="checkin-card-header">
                                    <el-avatar :size="32" :src="item.user?.avatar" />
                                    <div class="user-info">
                                        <div class="username">{{ item.user?.username }}</div>
                                        <div class="checkin-time">{{ formatDate(item.createdAt) }}</div>
                                    </div>
                                </div>
                                <div class="checkin-node-info" v-if="item.node">
                                    <el-tag size="small" type="info">打卡试卷: {{ item.node.title }}</el-tag>
                                </div>
                                <div class="checkin-card-content">
                                    <p class="content-text">{{ item.content }}</p>
                                    <div v-if="item.imageUrls && item.imageUrls.length" class="checkin-images-preview">
                                        <el-image v-for="(url, idx) in item.imageUrls.slice(0, 3)" :key="idx" :src="url"
                                            fit="cover" class="preview-img" />
                                        <div v-if="item.imageUrls.length > 3" class="more-images">+{{
                                            item.imageUrls.length - 3
                                            }}</div>
                                    </div>
                                </div>
                                <div class="checkin-card-footer">
                                    <span class="action-btn" @click.stop="handleLike(item)">
                                        <el-icon :class="{ liked: item.liked }">
                                            <Pointer />
                                        </el-icon> {{ item.likeCount || 0 }}
                                    </span>
                                    <span class="action-btn">
                                        <el-icon>
                                            <ChatDotRound />
                                        </el-icon> {{ item.commentCount || 0 }}
                                    </span>
                                    <span class="action-btn" @click.stop="handleFavorite(item)">
                                        <el-icon :class="{ favorited: item.favorited }">
                                            <Star />
                                        </el-icon>
                                    </span>
                                </div>
                            </el-card>
                        </div>
                        <div v-if="allCheckIns.length === 0" class="empty">暂无打卡，快去完成学习并打卡吧</div>
                    </div>
                </el-tab-pane>
            </el-tabs>
        </div>

        <el-dialog v-model="showPostDialog" title="发表讨论" width="600px">
            <el-form :model="postForm" label-width="60px">
                <el-form-item label="标题" required>
                    <el-input v-model="postForm.title" />
                </el-form-item>
                <el-form-item label="内容" required>
                    <md-editor v-model="postForm.content" style="height: 300px" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="showPostDialog = false">取消</el-button>
                <el-button type="primary" @click="submitPost">发布</el-button>
            </template>
        </el-dialog>

        <!-- Payment QR Dialog -->
        <el-dialog v-model="showPaymentDialog" title="活动报名支付" width="380px" center @close="stopPolling"
            class="activity-pay-dialog">
            <div class="pay-body">
                <div class="pay-resource">
                    <div class="resource-label">正在报名活动：</div>
                    <div class="resource-name">{{ activity.title }}</div>
                </div>

                <div class="pay-amount-section">
                    <span class="label">应付金额</span>
                    <span class="amount">¥{{ activity.price }}</span>
                </div>

                <div class="qr-container">
                    <div class="pay-method-header">
                        <img src="@/assets/weixin_pay_logo.png" class="wx-logo" alt="WeChat Pay" />
                        <span class="pay-text">微信扫码支付</span>
                    </div>

                    <div class="qr-wrapper">
                        <div class="qr-border">
                            <el-image :src="paymentQrcodeStr" class="qr-code" />
                        </div>
                        <p class="pay-instruction">请使用微信“扫一扫”完成支付</p>
                    </div>

                    <div class="poll-status">
                        <el-icon v-if="polling" class="is-loading">
                            <Loading />
                        </el-icon>
                        <span v-else><el-icon>
                                <CircleCheckFilled />
                            </el-icon></span>
                        支付成功后将自动跳转
                    </div>
                </div>
            </div>
        </el-dialog>

        <!-- Video Player Dialog -->
        <el-dialog v-model="showVideoPlayer" :title="currentVideo?.title" width="80%">
            <div class="video-container" v-if="currentVideo">
                <video :src="currentVideo.videoUrl" controls autoplay style="width: 100%"></video>
            </div>
        </el-dialog>

        <!-- Check-In Dialog -->
        <el-dialog v-model="showCheckInDialog" title="学习打卡" width="500px">
            <el-form label-position="top">
                <el-form-item label="打卡心得/演算过程">
                    <el-input v-model="checkInForm.content" type="textarea" :rows="4"
                        placeholder="写下你的思路、遇到的问题，或者简单的打卡宣言..."></el-input>
                </el-form-item>
                <el-form-item label="上传草稿/演算图片 (可选，每张限制1M内)">
                    <el-upload class="checkin-uploader" :action="getUploadAction()" :headers="uploadHeaders"
                        :data="{ type: 'checkin' }" list-type="picture-card" :file-list="checkInFileList"
                        :on-remove="handleCheckInRemove" :on-success="handleCheckInUploadSuccess"
                        :before-upload="beforeUpload" multiple accept="image/*">
                        <el-icon>
                            <Plus />
                        </el-icon>
                    </el-upload>
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="showCheckInDialog = false">取消</el-button>
                    <el-button type="primary" @click="submitCheckIn" :loading="submittingCheckIn">
                        提交打卡
                    </el-button>
                </span>
            </template>
        </el-dialog>

    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { View, User, Document, CircleCheckFilled, Lock, VideoPlay, Loading, Check, Edit, Plus, Pointer, ChatDotRound, Star } from '@element-plus/icons-vue'
import api, { getToken } from '@/utils/api'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import dayjs from 'dayjs'

const route = useRoute()
const router = useRouter()
const activityId = route.params.id

const loading = ref(false)
const activeTab = ref('intro')
const isEnrolled = ref(false)
const activity = ref({})
const nodes = ref([])
const videos = ref([])
const posts = ref([])
const postForm = ref({ title: '', content: '' })

const defaultCover = "https://via.placeholder.com/400x225?text=Math+Course"
const defaultVideoCover = "https://via.placeholder.com/200x112?text=Play"

const fetchActivityDetail = async () => {
    const res = await api.get(`/api/activities/${activityId}`)
    activity.value = res.data.activity || {}
    isEnrolled.value = !!res.data.isEnrolled
}

const fetchNodes = async () => {
    const nodeRes = await api.get(`/api/activities/${activityId}/nodes`)
    nodes.value = nodeRes.data.nodes || []
}

const fetchVideos = async () => {
    const videoRes = await api.get(`/api/activities/${activityId}/videos`)
    videos.value = videoRes.data.videos || []
}

const fetchDiscussion = async () => {
    const postRes = await api.get(`/api/activities/${activityId}/discussion`)
    posts.value = postRes.data.posts || []
}

const fetchData = async () => {
    loading.value = true
    try {
        await fetchActivityDetail()
        await Promise.all([fetchNodes(), fetchVideos(), fetchDiscussion(), fetchCheckInWall()])
        await fetchAllCheckIns()
    } catch (e) {
        ElMessage.error(e.response?.data?.error || '获取活动详情失败')
    } finally {
        loading.value = false
    }
}

// Enrollment & Payment
const showPostDialog = ref(false)
const showPaymentDialog = ref(false)
const paymentQrcodeStr = ref('')
const outTradeNo = ref('')
const polling = ref(false)
let pollTimer = null

// Check-In Feature
const checkIns = ref({}) // map of nodeId -> checkIn data
const showCheckInDialog = ref(false)
const checkInForm = ref({ content: '', imageUrls: [] })
const checkInFileList = ref([]) // For el-upload ui bindings
const submittingCheckIn = ref(false)
const currentCheckInNodeId = ref(null)

// Check-in Wall
const allCheckIns = ref([])
const getUploadAction = () => {
    return (api.defaults.baseURL || '') + '/api/upload/image'
}
const uploadHeaders = computed(() => {
    return { Authorization: `Bearer ${getToken()}` }
})

const handleEnroll = async () => {
    if (!localStorage.getItem('token')) {
        return ElMessage.warning('请先登录后再报名')
    }
    try {
        const res = await api.post(`/api/activities/${activityId}/enroll`)
        if (res.data.free) {
            ElMessage.success('报名成功！')
            isEnrolled.value = true
            await fetchData()
        } else {
            paymentQrcodeStr.value = res.data.qr_url
            outTradeNo.value = res.data.out_trade_no
            showPaymentDialog.value = true
            startPolling()
        }
    } catch (e) {
        ElMessage.error(e.response?.data?.error || '报名请求失败')
    }
}

// Social logic
const fetchCheckInWall = async () => {
    try {
        const res = await api.get(`/api/activities/${activityId}/checkins`)
        allCheckIns.value = res.data.checkIns || []
    } catch (e) { }
}

const viewCheckInDetail = (item) => {
    router.push('/checkins/' + item.id)
}

const handleLike = async (item) => {
    try {
        const res = await api.post(`/api/checkins/${item.id}/like`)
        item.liked = res.data.liked
        const currentLikeCount = Number(item.likeCount || 0)
        item.likeCount = res.data.liked ? currentLikeCount + 1 : Math.max(0, currentLikeCount - 1)
        ElMessage.success(res.data.liked ? '已点赞' : '已取消点赞')
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const handleFavorite = async (item) => {
    try {
        const res = await api.post(`/api/checkins/${item.id}/favorite`)
        item.favorited = res.data.favorited
        ElMessage.success(res.data.favorited ? '已收藏' : '已取消收藏')
    } catch (e) {
        ElMessage.error('操作失败')
    }
}

const startPolling = () => {
    stopPolling()
    polling.value = true
    pollTimer = setInterval(async () => {
        try {
            const res = await api.get(`/api/pay/status?outTradeNo=${outTradeNo.value}`)
            if (res.data.code === 1) {
                stopPolling()
                showPaymentDialog.value = false
                ElMessage.success('支付成功！')
                isEnrolled.value = true
                await fetchData()
            }
        } catch (e) { }
    }, 2000)
}

const stopPolling = () => {
    if (pollTimer) {
        clearInterval(pollTimer)
        pollTimer = null
    }
    polling.value = false
}

// Content interactions
const goPaper = (node) => {
    if (node.paperId) {
        router.push({ path: `/papers/${node.paperId}`, query: { activityId: activityId, nodeId: node.id } })
    } else if (node.fileUrl) {
        window.open(node.fileUrl, '_blank')
    }
}

// Activity Check-In Logic
const fetchAllCheckIns = async () => {
    const nextCheckIns = {}
    const promises = []
    nodes.value.forEach(chapter => {
        chapter.children?.forEach(section => {
            section.children?.forEach(paper => {
                promises.push(
                    api.get(`/api/activities/${activityId}/nodes/${paper.id}/checkin`)
                        .then(res => {
                            if (res.data.checkIn) {
                                nextCheckIns[paper.id] = res.data.checkIn
                            }
                        }).catch(() => { })
                )
            })
        })
    })
    await Promise.all(promises)
    checkIns.value = nextCheckIns
}

const hasCheckedIn = (nodeId) => {
    return !!checkIns.value[nodeId]
}

const openCheckInDialog = (nodeId) => {
    currentCheckInNodeId.value = nodeId
    const existing = checkIns.value[nodeId]
    if (existing) {
        checkInForm.value = {
            content: existing.content || '',
            imageUrls: existing.imageUrls || (existing.imageUrl ? [existing.imageUrl] : [])
        }
    } else {
        checkInForm.value = { content: '', imageUrls: [] }
    }
    // populate UI file list
    checkInFileList.value = checkInForm.value.imageUrls.map(url => ({
        name: url.split('/').pop(),
        url,
        response: { url } // Mock response to keep it consistent when removing
    }))
    showCheckInDialog.value = true
}

const compressImage = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (e) => {
            const img = new Image()
            img.src = e.target.result
            img.onload = () => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')

                // Max size limit for compress
                const MAX_WIDTH = 1920
                const MAX_HEIGHT = 1080
                let width = img.width
                let height = img.height

                if (width > height) {
                    if (width > MAX_WIDTH) {
                        height *= MAX_WIDTH / width
                        width = MAX_WIDTH
                    }
                } else {
                    if (height > MAX_HEIGHT) {
                        width *= MAX_HEIGHT / height
                        height = MAX_HEIGHT
                    }
                }

                canvas.width = width
                canvas.height = height
                ctx.drawImage(img, 0, 0, width, height)

                // Adjust quality until it's less than 1MB
                let quality = 0.9
                let compressedDataUrl = canvas.toDataURL('image/jpeg', quality)

                // Rough estimate of size in MB: length * 3 / 4 bytes
                while (compressedDataUrl.length * 0.75 > 1024 * 1024 && quality > 0.1) {
                    quality -= 0.1
                    compressedDataUrl = canvas.toDataURL('image/jpeg', quality)
                }

                canvas.toBlob((blob) => {
                    const compressedFile = new File([blob], file.name, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    })
                    resolve(compressedFile)
                }, 'image/jpeg', quality)
            }
            img.onerror = reject
        }
        reader.onerror = reject
    })
}

const beforeUpload = async (file) => {
    const isImage = file.type.startsWith('image/')
    if (!isImage) {
        ElMessage.error('只能上传图片文件!')
        return false
    }
    // Perform compression
    try {
        const compressedFile = await compressImage(file)
        return compressedFile
    } catch (err) {
        ElMessage.error('图片压缩失败，将上传原图')
        return file
    }
}

const handleCheckInUploadSuccess = (res, uploadFile, uploadFiles) => {
    checkInFileList.value = uploadFiles
}

const handleCheckInRemove = (uploadFile, uploadFiles) => {
    checkInFileList.value = uploadFiles
}

const submitCheckIn = async () => {
    // Sync file list URLs into checkInForm before submit
    checkInForm.value.imageUrls = checkInFileList.value.map(f => f.response?.url || f.url).filter(Boolean)

    if (!checkInForm.value.content && checkInForm.value.imageUrls.length === 0) {
        return ElMessage.warning('请输入内容或上传图片才能打卡')
    }
    submittingCheckIn.value = true
    try {
        const nodeId = currentCheckInNodeId.value
        const res = await api.post(`/api/activities/${activityId}/nodes/${nodeId}/checkin`, {
            content: checkInForm.value.content,
            imageUrls: checkInForm.value.imageUrls
        })
        ElMessage.success('打卡成功')
        checkIns.value[nodeId] = res.data.checkIn
        showCheckInDialog.value = false
    } catch (e) {
        ElMessage.error('打卡请求失败')
    } finally {
        submittingCheckIn.value = false
    }
}

const showVideoPlayer = ref(false)
const currentVideo = ref(null)
const playVideo = (v) => {
    currentVideo.value = v
    showVideoPlayer.value = true
}

// Discussion
const submitPost = async () => {
    if (!postForm.value.title || !postForm.value.content) return ElMessage.warning('请填满内容')
    try {
        await api.post('/api/forum/posts', {
            title: postForm.value.title,
            content: postForm.value.content,
            type: 'article',
            activityId: Number(activityId)
        })
        ElMessage.success('发布成功')
        showPostDialog.value = false
        postForm.value = { title: '', content: '' }
        await fetchDiscussion()
    } catch (e) { ElMessage.error('发布失败') }
}

const goPost = (post) => router.push(`/forum/${post.id}`)

const formatDuration = (s) => {
    const totalSeconds = Number(s)
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
        return '未知时长'
    }
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return `${minutes}:${String(seconds).padStart(2, '0')}`
}
const formatDate = (d) => dayjs(d).format('YYYY-MM-DD')

onMounted(fetchData)
onUnmounted(stopPolling)
</script>

<style scoped>
.activity-detail-page {
    min-height: 100vh;
    background: #ffffff;
    /* Unified white background */
    font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.apple-banner {
    padding: 30px 0 40px;
    /* Reduced bottom padding since there's no color break */
    /* Removed background-color and border-bottom to let it blend */
}

.container {
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 20px;
}

.banner-inner {
    position: relative;
    z-index: 10;
}

.apple-breadcrumb {
    margin-bottom: 24px;
}

.apple-breadcrumb :deep(.el-breadcrumb__inner) {
    color: #86868b !important;
    font-size: 13px;
    /* Slightly smaller */
    font-weight: 500;
}

.apple-breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
    color: #1d1d1f !important;
    font-weight: 600;
}

.apple-breadcrumb :deep(.el-breadcrumb__separator) {
    color: #d2d2d7 !important;
}

.apple-hero-card {
    background: #ffffff;
    border-radius: 20px;
    /* Rounded but slightly less bulbous */
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03);
    overflow: hidden;
}

.hero-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    /* Changed from stretch so columns define own heights based on content */
}

.cover-col {
    padding: 24px !important;
    background: #ffffff;
    position: relative;
    display: flex;
    /* Centers the image if it doesn't span full height */
    align-items: center;
    justify-content: center;
}

.apple-cover {
    width: 100%;
    aspect-ratio: 16/9;
    /* Force consistent ratio */
    object-fit: contain;
    /* Ensure the entire original image format is visible without hard cropping */
    background: #f5f5f7;
    border-radius: 12px;
    display: block;
}

.info-col {
    padding: 24px 32px !important;
    /* Reduced padding from 32px 40px */
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.tags-wrapper {
    margin-bottom: 16px;
    /* Reduced */
    display: flex;
    gap: 8px;
}

.apple-tag {
    font-size: 12px;
    font-weight: 600;
    color: #0071e3;
    background: rgba(0, 113, 227, 0.08);
    padding: 4px 10px;
    /* Thinner */
    border-radius: 6px;
    letter-spacing: 0.02em;
}

.apple-title {
    font-size: 28px;
    /* Reduced from 40px */
    font-weight: 700;
    color: #1d1d1f;
    line-height: 1.25;
    margin: 0 0 12px 0;
    /* Reduced */
    letter-spacing: -0.01em;
}

.apple-meta {
    font-size: 13px;
    /* Reduced from 15px */
    font-weight: 500;
    color: #86868b;
    margin: 0 0 32px 0;
    /* Reduced from 48px */
    display: flex;
    align-items: center;
    gap: 6px;
}

.dot-separator {
    margin: 0 8px;
    font-size: 12px;
    color: #d2d2d7;
}

.apple-action-area {
    margin-top: auto;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    /* Changed from space-between since price is gone */
    padding-top: 20px;
    border-top: 1px solid #f5f5f7;
}

.apple-btn {
    background-color: #0071e3 !important;
    border-color: #0071e3 !important;
    color: #fff !important;
    border-radius: 980px !important;
    padding: 10px 32px !important;
    /* Slightly wider button */
    font-size: 14px !important;
    font-weight: 600 !important;
    height: auto !important;
    line-height: 1.2 !important;
    transition: all 0.2s cubic-bezier(0.25, 0.1, 0.25, 1) !important;
}

.apple-btn:hover {
    background-color: #0077ed !important;
    transform: scale(1.02);
}

.apple-enrolled-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #1d1d1f;
    font-size: 14px;
    font-weight: 600;
    background: #f5f5f7;
    padding: 8px 24px;
    border-radius: 980px;
}

.main-content {
    margin-top: 30px;
    padding-bottom: 60px;
}

.card-box {
    background: #ffffff;
    padding: 32px 0;
    /* Remove side padding so it aligns with the tabs */
    border-radius: 20px;
    min-height: 300px;
}

.locked-tip {
    text-align: center;
    padding: 60px 0;
    color: #94a3b8;
}

.locked-tip .el-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.chapter-item {
    margin-bottom: 30px;
}

.chapter-title {
    font-size: 20px;
    border-left: 4px solid #3b82f6;
    padding-left: 12px;
    margin-bottom: 20px;
}

.section-item {
    margin-left: 20px;
    margin-bottom: 15px;
}

.section-title {
    font-size: 16px;
    color: #475569;
    margin-bottom: 10px;
}

.paper-item {
    margin-left: 20px;
    padding: 12px 16px;
    color: #4b5563;
    font-size: 14px;
    border-radius: 8px;
    background-color: #f9fafb;
    margin-bottom: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid transparent;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
}

.paper-title-area {
    display: flex;
    align-items: center;
    flex-grow: 1;
}

.paper-item:hover {
    background-color: #f3f4f6;
    border-color: #e5e7eb;
}

.paper-name {
    margin: 0 12px;
    font-weight: 500;
}

.paper-actions {
    display: flex;
    gap: 16px;
    align-items: center;
}

.video-grid .videos {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
}

.video-card {
    cursor: pointer;
    border-radius: 10px;
}

.video-thumb {
    position: relative;
    aspect-ratio: 16/9;
}

.play-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 40px;
    opacity: 0;
    transition: opacity 0.3s;
}

.video-card:hover .play-overlay {
    opacity: 1;
}

.video-title {
    font-size: 14px;
    font-weight: bold;
    margin: 8px 0 4px;
}

.video-meta {
    font-size: 12px;
    color: #94a3b8;
}

.discussion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.post-item {
    display: flex;
    gap: 15px;
    padding: 15px 0;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
}

.post-item:hover .post-title {
    color: #3b82f6;
}

.post-title {
    font-weight: bold;
    margin-bottom: 5px;
}

.post-meta {
    font-size: 12px;
    color: #94a3b8;
}

.pay-body {
    text-align: center;
}

.pay-amount {
    font-size: 32px;
    font-weight: bold;
    color: #ef4444;
    margin-bottom: 10px;
}

.pay-resource {
    margin-bottom: 20px;
}

.pay-resource .resource-label {
    font-size: 13px;
    color: #86868b;
    margin-bottom: 4px;
}

.pay-resource .resource-name {
    font-size: 16px;
    font-weight: 600;
    color: #1d1d1f;
}

.pay-amount-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background: #fbfbfd;
    border-radius: 12px;
    margin-bottom: 20px;
}

.pay-amount-section .label {
    color: #86868b;
    font-size: 14px;
}

.pay-amount-section .amount {
    font-size: 24px;
    font-weight: 700;
    color: #f56c6c;
}

.qr-container {
    background: #ffffff;
    border: 1px solid #f2f2f1;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
}

.pay-method-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-bottom: 15px;
}

.wx-logo {
    height: 20px;
}

.pay-text {
    font-size: 15px;
    font-weight: 700;
    color: #07c160;
}

.qr-wrapper {
    margin-bottom: 15px;
}

.qr-border {
    display: inline-block;
    padding: 8px;
    background: #fff;
    border: 1px solid #eeeeee;
    border-radius: 10px;
}

.qr-code {
    width: 160px;
    height: 160px;
    display: block;
}

.pay-instruction {
    margin-top: 10px;
    font-size: 12px;
    color: #86868b;
}

.poll-status {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #07c160;
    font-size: 13px;
    font-weight: 500;
    background: #f0fdf4;
    padding: 8px;
    border-radius: 8px;
}

:deep(.activity-pay-dialog) {
    border-radius: 18px;
}

:deep(.activity-pay-dialog .el-dialog__header) {
    margin-right: 0;
    padding-top: 20px;
}

:deep(.activity-pay-dialog .el-dialog__title) {
    font-weight: 700;
}

.empty {
    text-align: center;
    color: #94a3b8;
    padding: 40px 0;
}

/* Record Wall styles */
.checkin-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 20px;
    margin-top: 20px;
}

.checkin-card {
    cursor: pointer;
    border-radius: 12px;
}

.checkin-card-header {
    display: flex;
    gap: 10px;
    margin-bottom: 12px;
}

.checkin-card-header .username {
    font-weight: bold;
    font-size: 14px;
}

.checkin-card-header .checkin-time {
    font-size: 12px;
    color: #94a3b8;
}

.checkin-node-info {
    margin-bottom: 10px;
}

.checkin-card-content .content-text {
    font-size: 14px;
    color: #334155;
    margin-bottom: 12px;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.checkin-images-preview {
    display: flex;
    gap: 5px;
    margin-bottom: 12px;
}

.preview-img {
    width: 60px;
    height: 60px;
    border-radius: 4px;
}

.more-images {
    width: 60px;
    height: 60px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: #64748b;
    border-radius: 4px;
}

.checkin-card-footer {
    display: flex;
    justify-content: space-between;
    padding-top: 10px;
    border-top: 1px solid #f1f5f9;
}

.action-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 13px;
    color: #64748b;
}

.action-btn .liked {
    color: #409eff;
}

.action-btn .favorited {
    color: #f59e0b;
}

/* Detail Dialog */
.checkin-detail-dialog .detail-header {
    display: flex;
    gap: 15px;
    margin-bottom: 20px;
}

.checkin-detail-dialog .user-meta .name {
    font-weight: bold;
    font-size: 18px;
}

.checkin-detail-dialog .user-meta .time {
    color: #94a3b8;
    font-size: 14px;
}

.checkin-detail-dialog .detail-content {
    margin-bottom: 24px;
}

.checkin-detail-dialog .node-tag {
    margin-bottom: 10px;
}

.checkin-detail-dialog .image-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    margin-top: 15px;
}

.comment-section {
    margin-top: 30px;
    border-top: 1px solid #f1f5f9;
    padding-top: 20px;
}

.comment-input {
    margin: 15px 0 20px;
}

/* Stale comment styles removed */

/* Upload Check-in styles */
:deep(.checkin-uploader .el-upload--picture-card) {
    width: 100px;
    height: 100px;
}

:deep(.checkin-uploader .el-upload-list--picture-card .el-upload-list__item) {
    width: 100px;
    height: 100px;
}
</style>
